import { useState, useEffect } from "react";
import { Table, Button, Form, Row, Col, Alert } from "react-bootstrap";

const Customers = () => {
  const backendURI = process.env.REACT_APP_BK_URL;

  const emptyForm = {
    name: "",
    email: "",
    phone: "",
    address: "",
  };

  const [data, setData] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [error, setError] = useState("");

  const headers = {
    "Content-Type": "application/json;charset=UTF-8",
    Authorization: `Bearer ${localStorage.token}`,
  };

  const loadCustomers = () => {
    fetch(`${backendURI}/api/customers`, {
      method: "GET",
      headers,
    })
      .then((res) => res.json())
      .then((json) => {
        if (Array.isArray(json)) {
          setData(json);
        } else {
          setData([]);
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load customers");
      });
  };

  useEffect(() => {
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendURI]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingCustomerId(null);
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name || !formData.phone) {
      setError("Name and phone are required");
      return;
    }

    const isEditing = Boolean(editingCustomerId);

    const url = isEditing
      ? `${backendURI}/api/customers/${editingCustomerId}`
      : `${backendURI}/api/customers`;

    const method = isEditing ? "PUT" : "POST";

    fetch(url, {
      method,
      headers,
      body: JSON.stringify(formData),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Request failed");
        }

        return res.json();
      })
      .then(() => {
        resetForm();
        loadCustomers();
      })
      .catch((err) => {
        console.error(err);
        setError(isEditing ? "Failed to update customer" : "Failed to add customer");
      });
  };

  const handleEdit = (customer) => {
    setEditingCustomerId(customer.id);

    setFormData({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
    });

    setError("");
  };

  const handleDelete = (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this customer?"
    );

    if (!confirmDelete) return;

    fetch(`${backendURI}/api/customers/${id}`, {
      method: "DELETE",
      headers,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Delete failed");
        }

        return res.json();
      })
      .then(() => {
        loadCustomers();
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to delete customer");
      });
  };

  return (
    <div>
      <h3 className="mb-3">Customers</h3>

      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit} className="mb-4">
        <Row>
          <Col md={3}>
            <Form.Group className="mb-2">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Customer name"
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group className="mb-2">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Customer email"
              />
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group className="mb-2">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone number"
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group className="mb-2">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Customer address"
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="mt-2">
          <Button variant="primary" type="submit">
            {editingCustomerId ? "Update Customer" : "Add Customer"}
          </Button>

          {editingCustomerId && (
            <Button
              variant="secondary"
              type="button"
              className="ms-2"
              onClick={resetForm}
            >
              Cancel Edit
            </Button>
          )}
        </div>
      </Form>

      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>Id</th>
            <th>Name</th>
            <th>Email</th>
            <th>Ph No</th>
            <th>Address</th>
            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan="6" className="text-center">
                No customers found
              </td>
            </tr>
          ) : (
            data.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.name}</td>
                <td>{item.email}</td>
                <td>{item.phone}</td>
                <td>{item.address}</td>
                <td>
                  <Button
                    variant="warning"
                    size="sm"
                    className="me-2"
                    onClick={() => handleEdit(item)}
                  >
                    Edit
                  </Button>

                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default Customers;
